import { useState } from 'react'

import { useCreatePasskey, usePasskeys, useRemovePasskey } from '../../hooks/queries'
import { usePreparedPasskeyOptions } from '../../hooks/passkeyOptions'
import { createPasskey, deviceName, isAbort, passkeyAvailable, type RegistrationOptions } from '../../lib/passkey'
import { rueckfrage } from '../../components/Rueckfrage'
import { useTexte } from '../../texte'
import { fehlertext } from '../../api/client'

/**
 * Passkeys on the own account (#21): the list, a button to create one, a
 * button per row to remove it.
 *
 * **The passkey is added, the password stays.** Whoever loses the phone
 * still has the way via e-mail and password; that is why it says so below
 * the list, and why there is no way here to abolish the password.
 *
 * **No silent button.** If this browser cannot do passkeys (no HTTPS, old
 * browser), that stands there as a sentence -- and the list nevertheless,
 * because the passkeys of the other devices still belong to the account.
 */
export function PasskeyCard() {
  const t = useTexte()
  const list = usePasskeys()
  const create = useCreatePasskey()
  const remove = useRemovePasskey()
  const [cancelled, setCancelled] = useState(false)
  const available = passkeyAvailable()
  // Fetched as the card opens: the tap below calls the browser at once
  // (`hooks/passkeyOptions.ts`) -- the same way as on the login page.
  const prepared = usePreparedPasskeyOptions<RegistrationOptions>('/api/auth/passkeys/options', available)

  const when = (iso: string): string =>
    new Date(iso).toLocaleDateString(t.allgemein.gebiet, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })

  return (
    <div className="card card--seite" data-karte="passkeys">
      <h2 className="card__titel">{t.kontokarte.passkeys}</h2>
      <p className="card__lead">{t.kontokarte.passkeysLead}</p>

      {list.data && list.data.length > 0 && (
        <ul className="passkeys">
          {list.data.map((p) => (
            <li className="passkeys__zeile" key={p.id} data-passkey={p.id}>
              <span className="passkeys__text">
                <span className="passkeys__name">{t.kontokarte.passkeyAngelegtAuf(p.name)}</span>
                <span className="passkeys__wann">
                  {t.kontokarte.passkeyAngelegtAm(when(p.created_at))}
                  {', '}
                  {p.last_used_at === null
                    ? t.kontokarte.passkeyNieBenutzt
                    : t.kontokarte.passkeyBenutzt(when(p.last_used_at))}
                </span>
              </span>
              <button
                type="button"
                className="button button--outline button--small"
                disabled={remove.isPending}
                onClick={async () => {
                  if (await rueckfrage(t.kontokarte.passkeyEntfernenFrage(p.name), t.kontokarte.passkeyEntfernen)) {
                    remove.mutate(p.id)
                  }
                }}
              >
                {t.kontokarte.passkeyEntfernen}
              </button>
            </li>
          ))}
        </ul>
      )}
      {list.data && list.data.length === 0 && (
        <p className="card__lead" data-passkey="keine">{t.kontokarte.passkeyKeine}</p>
      )}

      {cancelled && <p className="notice notice--info" role="status">{t.kontokarte.passkeyAbgebrochen}</p>}
      {create.isError && !isAbort(create.error) && (
        <p className="notice" role="alert">
          {fehlertext(create.error)}
        </p>
      )}
      {remove.isError && (
        <p className="notice" role="alert">
          {fehlertext(remove.error)}
        </p>
      )}

      {available ? (
        <button
          type="button"
          className="button"
          disabled={create.isPending}
          data-passkey="anlegen"
          onClick={() => {
            setCancelled(false)
            const name = deviceName() ?? t.kontokarte.passkeyGeraet
            const options = prepared.ready()
            const attestation = options !== null
              ? createPasskey(options, name)
              : prepared.promise().then((fetched) => createPasskey(fetched, name))
            attestation.then(() => prepared.spend(), () => {})
            create.mutate({ attestation }, {
              onError: (error) => setCancelled(isAbort(error)),
            })
          }}
        >
          {create.isPending ? t.allgemein.laeuft : t.kontokarte.passkeyAnlegen}
        </button>
      ) : (
        <p className="card__lead card__lead--knapp" data-passkey="unmoeglich">
          {t.kontokarte.passkeyNichtMoeglich}
        </p>
      )}
      <p className="field__hint feld__hinweis--abstand">
        {t.kontokarte.passkeyPasswortBleibt}
      </p>
    </div>
  )
}
